/**
 * Blog resources sidebar: predictive article suggestions (current blog only).
 * Uses Shopify predictive search API + Section Rendering (articles only).
 */
class BlogSidebarPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resultsContainer = this.querySelector('[data-blog-sidebar-predictive-results]');
    this.statusEl = this.querySelector('.blog-sidebar-predictive__status');
    this.blogHandle = (this.dataset.blogHandle || '').trim();
    this.emptyMessage = this.dataset.emptyMessage || '';
    const sidebarVariant = (this.dataset.sidebarVariant || 'resources').toLowerCase();
    const exBlogs = this.parseCsvList(this.dataset.excludedBlogHandles);
    const exTmpl = this.parseCsvList(this.dataset.excludedTemplateSuffixes);
    const exTags = this.parseCsvList(this.dataset.excludedTags);

    if (sidebarVariant === 'recipes') {
      this.excludedBlogHandles = exBlogs;
      this.excludedTemplateSuffixes = exTmpl.length ? exTmpl : ['resources'];
      this.excludedTags = exTags;
    } else {
      this.excludedBlogHandles = exBlogs.length ? exBlogs : ['recipes', 'recipe'];
      this.excludedTemplateSuffixes = exTmpl.length ? exTmpl : ['recipe'];
      this.excludedTags = exTags.length ? exTags : ['recipe', 'recipes', 'product', 'products'];
    }
    this.abortController = null;
    this.cachedResults = {};
    this.searchTerm = '';

    if (!this.input || !this.resultsContainer) return;

    this.boundOnInput = debounce(() => this.onInputChange(), 300);
    this.input.addEventListener('input', this.boundOnInput);
    this.input.addEventListener('focus', () => this.onFocus());
    this.addEventListener('focusout', (e) => this.onFocusOut(e));

    this.input.setAttribute('autocomplete', 'off');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-haspopup', 'listbox');
    if (this.resultsContainer.id) {
      this.input.setAttribute('aria-controls', this.resultsContainer.id);
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onInputChange() {
    const q = this.getQuery();
    this.searchTerm = q;

    if (!q.length) {
      this.closePanel();
      return;
    }

    this.getSearchResults(q);
  }

  onFocus() {
    const q = this.getQuery();
    if (q.length) this.getSearchResults(q);
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.closePanel();
    }, 0);
  }

  setSidebarPredictiveOpen(isOpen) {
    const aside = this.closest('.blog-res-sidebar');
    if (!aside) return;
    aside.classList.toggle('blog-res-sidebar--predictive-open', Boolean(isOpen));
  }

  closePanel() {
    this.resultsContainer.innerHTML = '';
    this.resultsContainer.hidden = true;
    this.resultsContainer.removeAttribute('data-open');
    this.resultsContainer.removeAttribute('data-loading');
    this.input.setAttribute('aria-expanded', 'false');
    this.setStatus('');
    this.setSidebarPredictiveOpen(false);
  }

  setStatus(text) {
    if (this.statusEl) this.statusEl.textContent = text || '';
  }

  /** @param {string | undefined} raw */
  parseCsvList(raw) {
    if (!raw || typeof raw !== 'string') return [];
    return raw
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
  }

  /** @param {string} href */
  parseBlogHandleFromHref(href) {
    if (!href) return '';
    try {
      const u = href.startsWith('http') ? new URL(href) : new URL(href, window.location.origin);
      const m = u.pathname.match(/\/blogs\/([^/]+)\//);
      return m ? m[1].toLowerCase() : '';
    } catch {
      const m = href.match(/\/blogs\/([^/]+)\//);
      return m ? m[1].toLowerCase() : '';
    }
  }

  /**
   * Resources/news sidebar only: drop recipes blog, recipe template, product-tagged posts, then current blog.
   * @param {HTMLElement} root
   */
  applyResourceFilters(root) {
    root.querySelectorAll('.blog-sidebar-predictive__item').forEach((li) => {
      const a = li.querySelector('a.blog-sidebar-predictive__link');
      if (!a) return;

      const href = a.getAttribute('href') || '';
      const urlHandle = this.parseBlogHandleFromHref(href);

      if (this.excludedBlogHandles.length && this.excludedBlogHandles.includes(urlHandle)) {
        li.remove();
        return;
      }

      if (this.blogHandle) {
        const prefix = `/blogs/${this.blogHandle}/`.toLowerCase();
        if (!href.toLowerCase().includes(prefix)) {
          li.remove();
          return;
        }
      }

      const tmpl = (a.getAttribute('data-article-template-suffix') || '').trim().toLowerCase();
      if (tmpl && this.excludedTemplateSuffixes.length && this.excludedTemplateSuffixes.includes(tmpl)) {
        li.remove();
        return;
      }

      if (this.excludedTags.length) {
        const tagsRaw = a.getAttribute('data-article-tags') || '';
        const tags = tagsRaw
          .split(',')
          .map((t) => t.trim().toLowerCase())
          .filter(Boolean);
        const hit = tags.some((t) => this.excludedTags.includes(t));
        if (hit) {
          li.remove();
        }
      }
    });
  }

  getPredictiveSearchUrl() {
    const base = window.routes?.predictive_search_url || '';
    return base;
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    if (this.cachedResults[queryKey]) {
      this.setSidebarPredictiveOpen(true);
      this.renderMarkup(this.cachedResults[queryKey], searchTerm);
      return;
    }

    const url = this.getPredictiveSearchUrl();
    if (!url) return;

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    this.resultsContainer.innerHTML = '';
    this.setStatus(this.dataset.loadingText || '');
    this.resultsContainer.hidden = false;
    this.resultsContainer.setAttribute('data-loading', '');
    this.setSidebarPredictiveOpen(true);

    const params = new URLSearchParams();
    params.set('q', searchTerm);
    params.set('section_id', 'predictive-search-blog-sidebar');
    params.append('resources[type]', 'article');
    params.set('resources[limit]', '10');

    fetch(`${url}?${params.toString()}`, { signal: this.abortController.signal })
      .then((res) => {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        let markup = '';
        const sectionEl = doc.querySelector('#shopify-section-predictive-search-blog-sidebar');
        if (sectionEl) {
          markup = sectionEl.innerHTML.trim();
        } else {
          const rootEl = doc.querySelector('#blog-sidebar-predictive-root');
          if (rootEl) markup = rootEl.outerHTML.trim();
        }
        this.cachedResults[queryKey] = markup;
        this.renderMarkup(markup, searchTerm);
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        this.resultsContainer.removeAttribute('data-loading');
        this.resultsContainer.innerHTML = `<p class="blog-sidebar-predictive__empty-message" role="status">${this.escapeHtml(this.emptyMessage)}</p>`;
        this.resultsContainer.hidden = false;
        this.resultsContainer.setAttribute('data-open', '');
        this.input.setAttribute('aria-expanded', 'true');
        this.setSidebarPredictiveOpen(true);
        this.setStatus('');
      });
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  renderMarkup(markup, searchTerm) {
    this.resultsContainer.removeAttribute('data-loading');
    if (!markup) {
      this.resultsContainer.innerHTML = `<p class="blog-sidebar-predictive__empty-message" role="status">${this.escapeHtml(this.emptyMessage)}</p>`;
      this.resultsContainer.hidden = false;
      this.resultsContainer.setAttribute('data-open', '');
      this.input.setAttribute('aria-expanded', 'true');
      this.setSidebarPredictiveOpen(true);
      this.setStatus(this.emptyMessage);
      return;
    }

    this.resultsContainer.innerHTML = markup;
    const root = this.resultsContainer.querySelector('#blog-sidebar-predictive-root');
    if (!root) {
      this.resultsContainer.innerHTML = `<p class="blog-sidebar-predictive__empty-message" role="status">${this.escapeHtml(this.emptyMessage)}</p>`;
      this.resultsContainer.hidden = false;
      this.resultsContainer.setAttribute('data-open', '');
      this.input.setAttribute('aria-expanded', 'true');
      this.setSidebarPredictiveOpen(true);
      this.setStatus(this.emptyMessage);
      return;
    }

    this.applyResourceFilters(root);

    const list = root.querySelector('.blog-sidebar-predictive__list');
    const hasItems = list && list.querySelector('.blog-sidebar-predictive__item');

    if (!hasItems) {
      root.innerHTML = `<p class="blog-sidebar-predictive__empty-message" role="status">${this.escapeHtml(this.emptyMessage)}</p>`;
    }

    this.resultsContainer.innerHTML = root.outerHTML;
    this.resultsContainer.hidden = false;
    this.resultsContainer.setAttribute('data-open', '');
    this.input.setAttribute('aria-expanded', 'true');
    this.setSidebarPredictiveOpen(true);

    const live = this.resultsContainer.querySelector('[data-predictive-search-live-region-count-value]');
    this.setStatus(live ? live.textContent.trim() : '');
  }
}

if (!customElements.get('blog-sidebar-predictive-search')) {
  customElements.define('blog-sidebar-predictive-search', BlogSidebarPredictiveSearch);
}
